let audioCtx: AudioContext | null = null;
let muted = false;

// lazy create (or resume) the shared AudioContext.
function getAudioContext(): AudioContext | null {
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return null;
    audioCtx = new AudioContextClass();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

// ── click ────────────────────────────────────────────────────────────────────
// currently unused in ui, old sound
export function click({ pitch = 1, gain = 0.16 } = {}): void {
  if (muted) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;

    // master bus — a touch of lowpass keeps everything soft, never clicky-harsh
    const masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 4200;
    lowpass.Q.value = 0.5;
    masterGain.connect(lowpass).connect(ctx.destination);

    // rounded body — sine with a short downward glide gives the "pop"
    const body = ctx.createOscillator();
    body.type = "sine";
    body.frequency.setValueAtTime(520 * pitch, t);
    body.frequency.exponentialRampToValueAtTime(230 * pitch, t + 0.07);
    const bodyGain = ctx.createGain();
    bodyGain.gain.setValueAtTime(0.0001, t);
    bodyGain.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    body.connect(bodyGain).connect(masterGain);
    body.start(t);
    body.stop(t + 0.18);

    // faint high tick — brightness on the attack so it feels precise
    const tick = ctx.createOscillator();
    tick.type = "sine";
    tick.frequency.setValueAtTime(1500 * pitch, t);
    tick.frequency.exponentialRampToValueAtTime(900 * pitch, t + 0.03);
    const tickGain = ctx.createGain();
    tickGain.gain.setValueAtTime(0.0001, t);
    tickGain.gain.exponentialRampToValueAtTime(gain * 0.35, t + 0.003);
    tickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    tick.connect(tickGain).connect(masterGain);
    tick.start(t);
    tick.stop(t + 0.06);
  } catch {
    // never let audio break the UI
  }
}

// ── thock ────────────────────────────────────────────────────────────────────
// also unused in ui, old sound
export function thock({ pitch = 1, gain = 0.3 } = {}): void {
  if (muted) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;

    // soft mallet attack — a brief, muffled noise tap
    const attackLen = Math.floor(ctx.sampleRate * 0.02);
    const attackBuf = ctx.createBuffer(1, attackLen, ctx.sampleRate);
    const attackData = attackBuf.getChannelData(0);
    for (let i = 0; i < attackLen; i++) {
      attackData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / attackLen, 3.0);
    }
    const attackSrc = ctx.createBufferSource();
    attackSrc.buffer = attackBuf;
    const attackLp = ctx.createBiquadFilter();
    attackLp.type = "lowpass";
    attackLp.frequency.value = 900 * pitch;
    const attackGain = ctx.createGain();
    attackGain.gain.value = gain * 0.5;
    attackSrc.connect(attackLp).connect(attackGain).connect(ctx.destination);
    attackSrc.start(t);
    attackSrc.stop(t + 0.025);

    // deep hollow body — low sine dropping to a sub-thud
    const body = ctx.createOscillator();
    body.type = "sine";
    body.frequency.setValueAtTime(120 * pitch, t);
    body.frequency.exponentialRampToValueAtTime(38 * pitch, t + 0.12);
    const bodyGain = ctx.createGain();
    bodyGain.gain.setValueAtTime(0.0001, t);
    bodyGain.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.30);
    body.connect(bodyGain).connect(ctx.destination);
    body.start(t);
    body.stop(t + 0.32);

    // hollow wooden resonance for the "cl-onk" colour
    const resonance = ctx.createOscillator();
    resonance.type = "triangle";
    resonance.frequency.setValueAtTime(230 * pitch, t);
    resonance.frequency.exponentialRampToValueAtTime(95 * pitch, t + 0.10);
    const resonanceGain = ctx.createGain();
    resonanceGain.gain.setValueAtTime(0.0001, t);
    resonanceGain.gain.exponentialRampToValueAtTime(gain * 0.55, t + 0.005);
    resonanceGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    resonance.connect(resonanceGain).connect(ctx.destination);
    resonance.start(t);
    resonance.stop(t + 0.2);
  } catch {
    // never let audio break the UI
  }
}

// ── tone ─────────────────────────────────────────────────────────────────────
// One deep, continuous, harmonious chord for opening / closing spiral card
export function tone({ open = true, gain = 0.1 } = {}): void {
  if (muted) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;

    const root = open ? 130.81 : 110.0;          // C3 (open) · A2 (close)
    const ratios = [1, 1.5, 2, 2.5, 3, 4];        // root · 5th · 8ve · maj3+2oct · 12th · 2×8ve
    const release = open ? 2.55 : 1.95;

    // warm master bus — gentle lowpass keeps the chord round, not harsh
    const masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 1900;
    lowpass.Q.value = 0.3;
    masterGain.connect(lowpass).connect(ctx.destination);

    // one shared envelope so the chord reads as a single continuous swell
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.0001, t);
    envelope.gain.linearRampToValueAtTime(gain, t + 0.12);
    envelope.gain.exponentialRampToValueAtTime(0.0001, t + release);
    envelope.connect(masterGain);

    // two slightly detuned sines per harmonic → rich, shimmering body
    ratios.forEach((ratio) => {
      const freq = root * ratio;
      [-3, 3].forEach((detuneCents) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.detune.value = detuneCents;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 1 / (ratios.length * 2);
        osc.connect(oscGain).connect(envelope);
        osc.start(t);
        osc.stop(t + release + 0.1);
      });
    });
  } catch {
    // never let audio break the UI
  }
}

// ── chime ────────────────────────────────────────────────────────────────────
// a big, positive chord for menu opening / closing
export function chime({ open = true, gain = 0.095 } = {}): void {
  if (muted) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;

    const root = open ? 196.0 : 174.61;               // G3 (open) · F3 (close)
    const ratios = [1, 1.25, 1.5, 2, 2.5, 3, 4];      // root · M3 · 5th · 8ve · M3+8ve · 12th · 2×8ve
    const release = open ? 3.6 : 2.8;

    const masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 2600;
    lowpass.Q.value = 0.3;
    masterGain.connect(lowpass).connect(ctx.destination);

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.0001, t);
    envelope.gain.linearRampToValueAtTime(gain, t + 0.16);
    envelope.gain.exponentialRampToValueAtTime(0.0001, t + release);
    envelope.connect(masterGain);

    ratios.forEach((ratio) => {
      const freq = root * ratio;
      [-3, 3].forEach((detuneCents) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.detune.value = detuneCents;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 1 / (ratios.length * 2);
        osc.connect(oscGain).connect(envelope);
        osc.start(t);
        osc.stop(t + release + 0.1);
      });
    });
  } catch {
    // never let audio break the UI
  }
}

// ── swish ────────────────────────────────────────────────────────────────────
// brief harmonious blip for switching the spiral / list view
export function swish({ up = true, gain = 0.12 } = {}): void {
  if (muted) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;

    const sweepLen = Math.floor(ctx.sampleRate * 0.22);
    const sweepBuf = ctx.createBuffer(1, sweepLen, ctx.sampleRate);
    const sweepData = sweepBuf.getChannelData(0);
    for (let i = 0; i < sweepLen; i++) {
      sweepData[i] = (Math.random() * 2 - 1) * (1 - i / sweepLen);
    }
    const sweepSrc = ctx.createBufferSource();
    sweepSrc.buffer = sweepBuf;
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.Q.value = 1.1;
    bandpass.frequency.setValueAtTime(up ? 500 : 1700, t);
    bandpass.frequency.exponentialRampToValueAtTime(up ? 1700 : 500, t + 0.2);
    const sweepGain = ctx.createGain();
    sweepGain.gain.setValueAtTime(0.0001, t);
    sweepGain.gain.exponentialRampToValueAtTime(gain * 0.5, t + 0.04);
    sweepGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    sweepSrc.connect(bandpass).connect(sweepGain).connect(ctx.destination);
    sweepSrc.start(t);
    sweepSrc.stop(t + 0.23);

    const blip = ctx.createOscillator();
    blip.type = "sine";
    const blipFreq = up ? 587.33 : 440;              // D5 up · A4 down
    blip.frequency.setValueAtTime(blipFreq, t);
    blip.frequency.exponentialRampToValueAtTime(up ? blipFreq * 1.18 : blipFreq / 1.18, t + 0.12);
    const blipGain = ctx.createGain();
    blipGain.gain.setValueAtTime(0.0001, t);
    blipGain.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    blipGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    blip.connect(blipGain).connect(ctx.destination);
    blip.start(t);
    blip.stop(t + 0.2);
  } catch {
    // never let audio break the UI
  }
}

export function setMuted(v: boolean): void {
  muted = v;
}
